import React, { useState, useEffect, useRef } from 'react';
import { Settings, Type, Palette, Image, ToggleLeft, ToggleRight, Save, Upload, RefreshCw, Check, Link2, Plus, Trash2, Globe } from 'lucide-react';
import API, { resolvePhoto } from '../../utils/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import DashboardLayout from '../../components/DashboardLayout';
import toast from 'react-hot-toast';
import './AdminSettings.css';

const TABS = [
  { id: 'identitas', label: 'Identitas & Teks', icon: <Type size={15} /> },
  { id: 'warna',     label: 'Warna & Tema',     icon: <Palette size={15} /> },
  { id: 'logo',      label: 'Logo & Gambar',    icon: <Image size={15} /> },
  { id: 'seo',       label: 'SEO',              icon: <Globe size={15} /> },
  { id: 'navigasi',  label: 'Navigasi & Link',  icon: <Link2 size={15} /> },
  { id: 'fitur',     label: 'Fitur On/Off',     icon: <ToggleLeft size={15} /> },
];

const COLOR_FIELDS = [
  { key: 'colorPrimary',    label: 'Utama (Primary)',   hint: 'Heading, aksen biru' },
  { key: 'colorAccent',     label: 'Aksen (Orange)',    hint: 'Tombol utama, badge' },
  { key: 'colorLink',       label: 'Tautan',            hint: 'Warna link' },
  { key: 'colorBackground', label: 'Latar Utama',       hint: 'Background halaman' },
  { key: 'colorBg2',        label: 'Latar Sekunder',    hint: 'Permukaan' },
  { key: 'colorBg3',        label: 'Latar Tersier',     hint: 'Input, chip, hover' },
  { key: 'colorCard',       label: 'Kartu/Panel',       hint: 'Background card' },
  { key: 'colorText',       label: 'Teks Utama',        hint: 'Teks konten' },
  { key: 'colorTextMuted',  label: 'Teks Sekunder',     hint: 'Teks pendukung' },
  { key: 'colorTextFaint',  label: 'Teks Samar',        hint: 'Teks redup' },
  { key: 'colorBorder',     label: 'Garis/Border',      hint: 'Pemisah (boleh rgba)' },
  { key: 'colorSidebar',    label: 'Sidebar Dashboard', hint: 'Panel sidebar dashboard' },
  { key: 'colorFooter',     label: 'Footer',            hint: 'Background footer' },
  { key: 'colorNavbarBg',   label: 'Navbar Artikel',    hint: 'Background navbar baca artikel' },
  { key: 'colorSuccess',    label: 'Sukses',            hint: 'Status berhasil' },
  { key: 'colorWarning',    label: 'Peringatan',        hint: 'Status menunggu' },
  { key: 'colorDanger',     label: 'Bahaya',            hint: 'Hapus/gagal' },
  { key: 'colorCtaBg',      label: 'CTA Latar',         hint: 'Blok "Daftar & Masuk" portal' },
  { key: 'colorCtaText',    label: 'CTA Teks',          hint: 'Teks pada blok CTA' },
  { key: 'colorAuthBg',     label: 'Latar Auth',        hint: 'Halaman login/register/lupa sandi' },
  { key: 'colorAuthCard',   label: 'Kartu Auth',        hint: 'Kartu form login dll' },
  { key: 'colorAuthFooter',     label: 'Footer Auth',        hint: 'Footer halaman login/register' },
  { key: 'colorAuthFooterText', label: 'Teks Footer Auth',   hint: 'Teks pada footer auth' },
  { key: 'colorAuthButton',     label: 'Tombol Auth',        hint: 'Tombol Masuk/Daftar/Kirim' },
  { key: 'colorAuthButtonText', label: 'Teks Tombol Auth',   hint: 'Teks pada tombol form auth' },
  { key: 'colorAuthLink',       label: 'Tautan Auth',        hint: 'Link teks dalam form auth' },
  { key: 'colorAuthInput',      label: 'Input Auth',         hint: 'Latar field input form auth' },
  { key: 'colorAuthInputBorder',label: 'Border Input Auth',  hint: 'Garis field input (boleh rgba)' },
  { key: 'colorInvoiceHeader',     label: 'Header Invoice',      hint: 'Latar header & total invoice' },
  { key: 'colorInvoiceHeaderText', label: 'Teks Header Invoice', hint: 'Teks pada header invoice' },
  { key: 'colorInvoiceCard',       label: 'Kertas Invoice',      hint: 'Latar dokumen invoice' },
  { key: 'colorInvoiceText',       label: 'Teks Invoice',        hint: 'Teks isi invoice' },
];

const DARK_COLOR_FIELDS = [
  { key: 'darkColorPrimary',    label: 'Utama (Primary)',  hint: 'Aksen biru' },
  { key: 'darkColorAccent',     label: 'Aksen (Orange)',   hint: 'Aksen oranye' },
  { key: 'darkColorLink',       label: 'Tautan',           hint: 'Warna link' },
  { key: 'darkColorBackground', label: 'Latar Utama',      hint: 'Background halaman' },
  { key: 'darkColorBg2',        label: 'Latar Sekunder',   hint: 'Permukaan/section' },
  { key: 'darkColorBg3',        label: 'Latar Tersier',    hint: 'Input, chip, hover' },
  { key: 'darkColorCard',       label: 'Kartu/Panel',      hint: 'Background card' },
  { key: 'darkColorText',       label: 'Teks Utama',       hint: 'Teks konten' },
  { key: 'darkColorTextMuted',  label: 'Teks Sekunder',    hint: 'Teks pendukung' },
  { key: 'darkColorTextFaint',  label: 'Teks Samar',       hint: 'Teks redup' },
  { key: 'darkColorBorder',     label: 'Garis/Border',     hint: 'Pemisah (boleh rgba)' },
  { key: 'darkColorSidebar',       label: 'Sidebar Dashboard', hint: 'Sidebar dashboard' },
  { key: 'darkColorPortalSidebar', label: 'Sidebar Portal',    hint: 'Drawer menu portal' },
  { key: 'darkColorFooter',        label: 'Footer',            hint: 'Footer portal' },
  { key: 'darkColorNavbarBg',      label: 'Navbar Artikel',    hint: 'Navbar baca artikel' },
  { key: 'darkColorSuccess',    label: 'Sukses',           hint: 'Status berhasil' },
  { key: 'darkColorWarning',    label: 'Peringatan',       hint: 'Status menunggu' },
  { key: 'darkColorDanger',     label: 'Bahaya',           hint: 'Hapus/gagal' },
  { key: 'darkColorCtaBg',      label: 'CTA Latar',        hint: 'Blok "Daftar & Masuk"' },
  { key: 'darkColorCtaText',    label: 'CTA Teks',         hint: 'Teks pada blok CTA' },
  { key: 'darkColorAuthBg',     label: 'Latar Auth',       hint: 'Halaman login dll' },
  { key: 'darkColorAuthCard',   label: 'Kartu Auth',       hint: 'Kartu form login' },
  { key: 'darkColorAuthFooter',     label: 'Footer Auth',      hint: 'Footer halaman login' },
  { key: 'darkColorAuthFooterText', label: 'Teks Footer Auth', hint: 'Teks footer auth' },
  { key: 'darkColorAuthButton',     label: 'Tombol Auth',      hint: 'Tombol Masuk/Daftar' },
  { key: 'darkColorAuthButtonText', label: 'Teks Tombol Auth', hint: 'Teks tombol form' },
  { key: 'darkColorAuthLink',       label: 'Tautan Auth',      hint: 'Link dalam form auth' },
  { key: 'darkColorAuthInput',      label: 'Input Auth',       hint: 'Latar field input' },
  { key: 'darkColorAuthInputBorder',label: 'Border Input Auth',hint: 'Garis field (boleh rgba)' },
  { key: 'darkColorInvoiceHeader',     label: 'Header Invoice',      hint: 'Latar header invoice' },
  { key: 'darkColorInvoiceHeaderText', label: 'Teks Header Invoice', hint: 'Teks header invoice' },
  { key: 'darkColorInvoiceCard',       label: 'Kertas Invoice',      hint: 'Latar dokumen invoice' },
  { key: 'darkColorInvoiceText',       label: 'Teks Invoice',        hint: 'Teks isi invoice' },
];

// Field warna yang boleh rgba → pakai input teks (color picker tak dukung rgba)
const RGBA_COLOR_KEYS = ['colorBorder', 'darkColorBorder', 'colorAuthInputBorder', 'darkColorAuthInputBorder'];

const FEATURE_FIELDS = [
  { key: 'featureRegistration',       label: 'Pendaftaran Akun',      desc: 'Izinkan pengguna baru mendaftar secara mandiri' },
  { key: 'featureAds',                label: 'Banner Iklan',          desc: 'Tampilkan banner iklan di portal' },
  { key: 'featureAdSubmission',       label: 'Pasang Iklan (Reader)', desc: 'Izinkan reader memasang iklan dari dashboard. Nonaktif: menu "Pasang Iklan" & "Iklan Saya" disembunyikan' },
  { key: 'featureGuestRead',          label: 'Baca Tanpa Login',      desc: 'Tamu dapat membaca artikel tanpa akun' },
  { key: 'featureArticleRequest',     label: 'Kirim Artikel',         desc: 'Izinkan reader mengirimkan artikel baru' },
  { key: 'featureArticleRequestFree', label: 'Pengajuan Artikel Gratis', desc: 'Artikel yang diajukan reader langsung masuk ke antrian editor tanpa pembayaran Midtrans' },
];

function Field({ label, hint, children }) {
  return (
    <div className="as-field">
      <label className="as-field-label">{label}</label>
      {hint && <div className="as-field-hint">{hint}</div>}
      {children}
    </div>
  );
}

function ColorSwatch({ value, onChange }) {
  const ref = useRef(null);
  return (
    <div className="as-color-swatch-wrap" onClick={() => ref.current?.click()}>
      <span className="as-color-preview" style={{ background: value }} />
      <span className="as-color-hex">{value}</span>
      <input
        ref={ref}
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
    </div>
  );
}

function UploadImageField({ label, hint, value, endpoint, onUploaded }) {
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await API.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(res.data);
      toast.success('Gambar berhasil diunggah.');
    } catch {
      toast.error('Gagal mengunggah gambar.');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="as-upload-field">
      <div className="as-field-label">{label}</div>
      {hint && <div className="as-field-hint">{hint}</div>}
      <div className="as-upload-row">
        {value
          ? <img src={resolvePhoto(value)} alt={label} className="as-upload-preview" />
          : <div className="as-upload-placeholder"><Image size={28} /></div>
        }
        <button type="button" className="as-upload-btn" onClick={() => ref.current?.click()} disabled={loading}>
          <Upload size={14} />
          {loading ? 'Mengunggah...' : 'Pilih File'}
        </button>
        <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      </div>
    </div>
  );
}

/* Editable list of {label, url} pairs */
function LinkListEditor({ value = [], onChange, addLabel = 'Tambah Link' }) {
  const add = () => onChange([...value, { label: '', url: '' }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const next = value.map((item, idx) => idx === i ? { ...item, [field]: val } : item);
    onChange(next);
  };
  return (
    <div className="as-link-list">
      {value.map((item, i) => (
        <div key={i} className="as-link-row">
          <input
            className="as-input"
            placeholder="Label"
            value={item.label}
            onChange={e => update(i, 'label', e.target.value)}
            style={{ flex: '0 0 160px' }}
          />
          <input
            className="as-input"
            placeholder="URL (https://...)"
            value={item.url}
            onChange={e => update(i, 'url', e.target.value)}
            style={{ flex: 1 }}
          />
          <button type="button" className="as-icon-btn as-icon-btn--danger" onClick={() => remove(i)}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" className="as-btn-add" onClick={add}>
        <Plus size={13} /> {addLabel}
      </button>
    </div>
  );
}

/* Editable list of strings (tags) */
function TagListEditor({ value = [], onChange, addLabel = 'Tambah Tag' }) {
  const add = () => onChange([...value, '']);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const update = (i, val) => onChange(value.map((t, idx) => idx === i ? val : t));
  return (
    <div className="as-link-list">
      {value.map((tag, i) => (
        <div key={i} className="as-link-row">
          <input
            className="as-input"
            placeholder="Contoh: Dosen"
            value={tag}
            onChange={e => update(i, e.target.value)}
            style={{ flex: 1 }}
          />
          <button type="button" className="as-icon-btn as-icon-btn--danger" onClick={() => remove(i)}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" className="as-btn-add" onClick={add}>
        <Plus size={13} /> {addLabel}
      </button>
    </div>
  );
}

/* Editable list of heroTopics {label, icon} */
function TopicListEditor({ value = [], onChange }) {
  const ICON_OPTIONS = ['FlaskConical', 'BookOpen', 'Cpu', 'Users', 'Factory', 'PenSquare', 'Star', 'Globe', 'Lightbulb', 'Award'];
  const add = () => onChange([...value, { label: '', icon: 'Star' }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const next = value.map((item, idx) => idx === i ? { ...item, [field]: val } : item);
    onChange(next);
  };
  return (
    <div className="as-link-list">
      {value.map((item, i) => (
        <div key={i} className="as-link-row">
          <input
            className="as-input"
            placeholder="Label topik"
            value={item.label}
            onChange={e => update(i, 'label', e.target.value)}
            style={{ flex: 1 }}
          />
          <select
            className="as-input"
            value={item.icon || ''}
            onChange={e => update(i, 'icon', e.target.value)}
            style={{ flex: '0 0 140px' }}
          >
            <option value="">-- Ikon --</option>
            {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
          </select>
          <button type="button" className="as-icon-btn as-icon-btn--danger" onClick={() => remove(i)}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" className="as-btn-add" onClick={add}>
        <Plus size={13} /> Tambah Topik
      </button>
    </div>
  );
}

export default function AdminSettings() {
  const { settings: ctxSettings, reload } = useSiteSettings();
  const [activeTab, setActiveTab] = useState('identitas');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setForm({ ...ctxSettings }); }, [ctxSettings]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setSeo = (key, val) => setForm(f => ({ ...f, seo: { ...(f.seo || {}), [key]: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.patch('/settings', form);
      await reload();
      setSaved(true);
      toast.success('Pengaturan berhasil disimpan.');
      setTimeout(() => setSaved(false), 2500);
    } catch {
      toast.error('Gagal menyimpan pengaturan.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm({ ...ctxSettings });
    toast('Form direset ke nilai tersimpan.', { icon: 'ℹ️' });
  };

  return (
    <DashboardLayout>
      <div className="as-page">
        <div className="as-header">
          <div className="as-header-left">
            <Settings size={20} />
            <div>
              <h1 className="as-title">Pengaturan Situs</h1>
              <p className="as-subtitle">Kelola tampilan, teks, dan fitur platform secara terpusat.</p>
            </div>
          </div>
          <div className="as-header-actions">
            <button className="as-btn-ghost" onClick={handleReset} disabled={saving}>
              <RefreshCw size={14} /> Reset
            </button>
            <button className="as-btn-primary" onClick={handleSave} disabled={saving}>
              {saved ? <Check size={14} /> : <Save size={14} />}
              {saving ? 'Menyimpan...' : saved ? 'Tersimpan' : 'Simpan'}
            </button>
          </div>
        </div>

        <div className="as-layout">
          <div className="as-tabs">
            {TABS.map(t => (
              <button
                key={t.id}
                className={`as-tab${activeTab === t.id ? ' active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <div className="as-panel">

            {activeTab === 'identitas' && (
              <div className="as-section">
                <div className="as-section-title">Identitas Platform</div>
                <div className="as-fields">
                  <Field label="Nama Situs" hint="Muncul di navbar, tab browser, dan meta">
                    <input className="as-input" value={form.siteName || ''} onChange={e => set('siteName', e.target.value)} />
                  </Field>
                  <Field label="Tagline" hint="Baris singkat di bawah nama situs">
                    <input className="as-input" value={form.siteTagline || ''} onChange={e => set('siteTagline', e.target.value)} />
                  </Field>
                  <Field label="Tagline Portal" hint='Tampil dengan tanda kutip di bawah logo portal'>
                    <input className="as-input" value={form.portalTagline || ''} onChange={e => set('portalTagline', e.target.value)} />
                  </Field>
                  <Field label="Ukuran Tagline Portal (px)" hint="Default 18">
                    <input className="as-input" type="number" min={10} max={48} value={form.portalTaglineSize ?? 18} onChange={e => set('portalTaglineSize', parseInt(e.target.value) || 18)} />
                  </Field>
                  <Field label="Deskripsi Situs" hint="Untuk meta description dan SEO">
                    <textarea className="as-textarea" rows={3} value={form.siteDescription || ''} onChange={e => set('siteDescription', e.target.value)} />
                  </Field>
                  <Field label="Email Kontak" hint="Tampil di footer">
                    <input className="as-input" type="email" value={form.contactEmail || ''} onChange={e => set('contactEmail', e.target.value)} />
                  </Field>
                  <Field label="Teks Hak Cipta" hint="Di footer, contoh: PT Inspira Teknologi Nusantara">
                    <input className="as-input" value={form.copyrightText || ''} onChange={e => set('copyrightText', e.target.value)} />
                  </Field>
                </div>

                <div className="as-section-title" style={{ marginTop: 32 }}>Footer</div>
                <div className="as-fields">
                  <Field label="Tagline Footer" hint="Deskripsi singkat di footer">
                    <textarea className="as-textarea" rows={3} value={form.footerTagline || ''} onChange={e => set('footerTagline', e.target.value)} />
                  </Field>
                </div>
              </div>
            )}

            {activeTab === 'warna' && (
              <div className="as-section">
                <div className="as-section-title">Skema Warna Mode Terang</div>
                <p className="as-section-desc">Perubahan warna berlaku di seluruh halaman setelah disimpan.</p>
                <div className="as-color-grid">
                  {COLOR_FIELDS.map(f => (
                    <div key={f.key} className="as-color-item">
                      <div className="as-field-label">{f.label}</div>
                      <div className="as-field-hint">{f.hint}</div>
                      {RGBA_COLOR_KEYS.includes(f.key)
                        ? <input className="form-input" value={form[f.key] || ''} onChange={e => set(f.key, e.target.value)} placeholder="rgba(...)" />
                        : <ColorSwatch value={form[f.key] || '#000000'} onChange={v => set(f.key, v)} />}
                    </div>
                  ))}
                </div>
                <div className="as-section-title" style={{ marginTop: 32 }}>Pratinjau Warna (Terang)</div>
                <div className="as-preview-strip">
                  {COLOR_FIELDS.map(f => (
                    <div key={f.key} className="as-preview-chip" style={{ background: form[f.key] || '#eee' }}>
                      <span>{f.label.split(' ')[1] || f.label}</span>
                    </div>
                  ))}
                </div>

                <div className="as-section-title" style={{ marginTop: 40 }}>Skema Warna Mode Gelap</div>
                <p className="as-section-desc">Warna yang dipakai saat pengguna mengaktifkan mode gelap.</p>
                <div className="as-color-grid">
                  {DARK_COLOR_FIELDS.map(f => (
                    <div key={f.key} className="as-color-item">
                      <div className="as-field-label">{f.label}</div>
                      <div className="as-field-hint">{f.hint}</div>
                      {f.key === 'darkColorBorder'
                        ? <input className="as-input" value={form[f.key] || ''} onChange={e => set(f.key, e.target.value)} placeholder="rgba(255,255,255,0.10)" />
                        : <ColorSwatch value={form[f.key] || '#000000'} onChange={v => set(f.key, v)} />}
                    </div>
                  ))}
                </div>
                <div className="as-preview-strip" style={{ marginTop: 16 }}>
                  {DARK_COLOR_FIELDS.filter(f => f.key !== 'darkColorBorder').map(f => (
                    <div key={f.key} className="as-preview-chip" style={{ background: form[f.key] || '#222', color: '#fff' }}>
                      <span>{f.label.split(' ')[0]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'logo' && (
              <div className="as-section">
                <div className="as-section-title">Logo & Gambar</div>
                <p className="as-section-desc">Format: PNG, JPG, SVG, WebP. Setiap penempatan bisa memakai logo berbeda. Kosongkan untuk memakai Logo Utama sebagai cadangan.</p>
                <div className="as-fields">
                  <UploadImageField
                    label="Logo Utama (cadangan)"
                    hint="Dipakai jika logo penempatan spesifik kosong."
                    value={form.logoUrl}
                    endpoint="/settings/upload-logo"
                    onUploaded={d => { set('logoUrl', d.logoUrl); reload(); }}
                  />

                  {/* Tiap slot: terang + gelap + ukuran */}
                  {[
                    { key: 'logoPortalUrl', darkKey: 'logoPortalDarkUrl', sizeKey: 'logoPortalSize', defSize: 72, max: 160, label: 'Logo Portal Utama', hint: 'Masthead portal berita.' },
                    { key: 'logoSidebarUrl', darkKey: 'logoSidebarDarkUrl', sizeKey: 'logoSidebarSize', defSize: 36, max: 120, label: 'Logo Sidebar Dashboard', hint: 'Sidebar dashboard (latar gelap).' },
                    { key: 'logoFooterUrl', darkKey: 'logoFooterDarkUrl', sizeKey: 'logoFooterSize', defSize: 40, max: 120, label: 'Logo Footer', hint: 'Footer portal.' },
                    { key: 'logoArticleNavbarUrl', darkKey: 'logoArticleNavbarDarkUrl', sizeKey: 'logoArticleNavbarSize', defSize: 32, max: 80, label: 'Logo Navbar Baca Artikel', hint: 'Navbar atas saat baca artikel.' },
                    { key: 'logoPortalSidebarUrl', darkKey: 'logoPortalSidebarDarkUrl', sizeKey: null, label: 'Logo Sidebar Portal', hint: 'Drawer menu portal.' },
                  ].map(slot => (
                    <div key={slot.key} style={{ borderBottom: '1px solid var(--ki-border)', paddingBottom: 14, marginBottom: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{slot.label} <span style={{ fontWeight: 400, color: 'var(--ki-text-faint)' }}>— {slot.hint}</span></div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <UploadImageField label="Mode Terang" hint="" value={form[slot.key]}
                          endpoint={`/settings/upload-logo-field/${slot.key}`}
                          onUploaded={d => { set(slot.key, d.url); reload(); }} />
                        <UploadImageField label="Mode Gelap" hint="Versi untuk latar gelap." value={form[slot.darkKey]}
                          endpoint={`/settings/upload-logo-field/${slot.darkKey}`}
                          onUploaded={d => { set(slot.darkKey, d.url); reload(); }} />
                        {slot.sizeKey && (
                          <div className="form-group" style={{ maxWidth: 140 }}>
                            <label className="form-label">Tinggi (px)</label>
                            <input className="form-input" type="number" min={20} max={slot.max} value={form[slot.sizeKey] || slot.defSize}
                              onChange={e => set(slot.sizeKey, parseInt(e.target.value) || slot.defSize)} />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Logo Auth (login/register/lupa sandi) terang + gelap */}
                  <div style={{ borderBottom: '1px solid var(--ki-border)', paddingBottom: 14, marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Logo Halaman Auth <span style={{ fontWeight: 400, color: 'var(--ki-text-faint)' }}>— login, register, lupa sandi</span></div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <UploadImageField label="Mode Terang" hint="" value={form.logoAuthUrl}
                        endpoint="/settings/upload-logo-field/logoAuthUrl" onUploaded={d => { set('logoAuthUrl', d.url); reload(); }} />
                      <UploadImageField label="Mode Gelap" hint="" value={form.logoAuthDarkUrl}
                        endpoint="/settings/upload-logo-field/logoAuthDarkUrl" onUploaded={d => { set('logoAuthDarkUrl', d.url); reload(); }} />
                    </div>
                  </div>

                  <UploadImageField
                    label="Logo Email"
                    hint="Tampil di header semua email. Kosong → pakai logo portal."
                    value={form.logoEmailUrl}
                    endpoint="/settings/upload-logo-field/logoEmailUrl"
                    onUploaded={d => { set('logoEmailUrl', d.url); reload(); }}
                  />

                  <UploadImageField
                    label="Logo Utama (cadangan global)"
                    hint="Dipakai jika logo slot di atas kosong."
                    value={form.logoDarkUrl}
                    endpoint="/settings/upload-logo-dark"
                    onUploaded={d => { set('logoDarkUrl', d.logoDarkUrl); reload(); }}
                  />
                  <UploadImageField
                    label="Favicon"
                    hint="Ikon browser tab. Rekomendasi: ICO atau PNG 32x32px."
                    value={form.faviconUrl}
                    endpoint="/settings/upload-favicon"
                    onUploaded={d => { set('faviconUrl', d.faviconUrl); reload(); }}
                  />
                </div>
              </div>
            )}

            {activeTab === 'seo' && (() => {
              const seo = form.seo || {};
              return (
              <div className="as-section">
                <div className="as-section-title">Dasar SEO</div>
                <p className="as-section-desc">Pengaturan meta untuk mesin pencari & pratinjau saat dibagikan. Kosongkan untuk memakai nilai dari Identitas.</p>
                <div className="as-fields">
                  <Field label="Meta Title Default" hint="Judul beranda & fallback. Kosong → nama situs.">
                    <input className="as-input" value={seo.metaTitle || ''} onChange={e => setSeo('metaTitle', e.target.value)} />
                  </Field>
                  <Field label="Template Judul" hint="%s = judul halaman, %site% = nama situs. Contoh: %s — %site%">
                    <input className="as-input" value={seo.titleTemplate || ''} onChange={e => setSeo('titleTemplate', e.target.value)} placeholder="%s — %site%" />
                  </Field>
                  <Field label="Meta Description Default" hint="Maks ~160 karakter. Kosong → deskripsi situs.">
                    <textarea className="as-textarea" rows={3} value={seo.metaDescription || ''} onChange={e => setSeo('metaDescription', e.target.value)} />
                  </Field>
                  <Field label="Keywords" hint="Dipisah koma (opsional, dampak kecil).">
                    <input className="as-input" value={seo.keywords || ''} onChange={e => setSeo('keywords', e.target.value)} placeholder="inovasi, riset, edukasi" />
                  </Field>
                  <Field label="Canonical Base URL" hint="Base URL situs untuk canonical & sitemap. Contoh: https://inspirasiya.com">
                    <input className="as-input" value={seo.canonicalBase || ''} onChange={e => setSeo('canonicalBase', e.target.value)} />
                  </Field>
                  <Field label="Locale" hint="Contoh: id_ID">
                    <input className="as-input" value={seo.locale || ''} onChange={e => setSeo('locale', e.target.value)} placeholder="id_ID" />
                  </Field>
                </div>

                <div className="as-section-title" style={{ marginTop: 32 }}>Social / Open Graph</div>
                <div className="as-fields">
                  <UploadImageField
                    label="Gambar Share Default (OG Image)"
                    hint="Tampil saat link dibagikan. Rekomendasi 1200×630px."
                    value={seo.defaultOgImage}
                    endpoint="/settings/upload-logo-field/logoUrl"
                    onUploaded={d => { setSeo('defaultOgImage', d.url); }}
                  />
                  <Field label="OG Type" hint="Biasanya: website">
                    <input className="as-input" value={seo.ogType || ''} onChange={e => setSeo('ogType', e.target.value)} placeholder="website" />
                  </Field>
                  <Field label="Twitter Card" hint="summary_large_image atau summary">
                    <input className="as-input" value={seo.twitterCard || ''} onChange={e => setSeo('twitterCard', e.target.value)} placeholder="summary_large_image" />
                  </Field>
                  <Field label="Twitter Site (@handle)" hint="Akun X situs">
                    <input className="as-input" value={seo.twitterSite || ''} onChange={e => setSeo('twitterSite', e.target.value)} placeholder="@inspirasiya" />
                  </Field>
                  <Field label="Twitter Creator (@handle)" hint="Akun X penulis/redaksi">
                    <input className="as-input" value={seo.twitterCreator || ''} onChange={e => setSeo('twitterCreator', e.target.value)} />
                  </Field>
                  <Field label="Facebook App ID" hint="Opsional">
                    <input className="as-input" value={seo.facebookAppId || ''} onChange={e => setSeo('facebookAppId', e.target.value)} />
                  </Field>
                </div>

                <div className="as-section-title" style={{ marginTop: 32 }}>Structured Data (JSON-LD)</div>
                <div className="as-fields">
                  <Field label="Nama Organisasi" hint="Untuk schema Organization. Kosong → nama situs.">
                    <input className="as-input" value={seo.organizationName || ''} onChange={e => setSeo('organizationName', e.target.value)} />
                  </Field>
                  <UploadImageField
                    label="Logo Organisasi (JSON-LD)"
                    hint="Logo untuk data terstruktur. Kosong → logo portal."
                    value={seo.organizationLogo}
                    endpoint="/settings/upload-logo-field/logoUrl"
                    onUploaded={d => { setSeo('organizationLogo', d.url); }}
                  />
                  <Field label="Nama Publisher" hint="Untuk schema artikel">
                    <input className="as-input" value={seo.publisherName || ''} onChange={e => setSeo('publisherName', e.target.value)} />
                  </Field>
                </div>

                <div className="as-section-title" style={{ marginTop: 32 }}>Indexing & Verifikasi</div>
                <div className="as-fields">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={seo.robotsIndex !== false} onChange={e => setSeo('robotsIndex', e.target.checked)} />
                    Izinkan mesin pencari mengindeks situs (robots: index)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={seo.robotsFollow !== false} onChange={e => setSeo('robotsFollow', e.target.checked)} />
                    Izinkan mengikuti tautan (robots: follow)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={seo.sitemapEnabled !== false} onChange={e => setSeo('sitemapEnabled', e.target.checked)} />
                    Aktifkan sitemap.xml
                  </label>
                  <Field label="Google Site Verification" hint="Kode meta dari Google Search Console">
                    <input className="as-input" value={seo.googleVerification || ''} onChange={e => setSeo('googleVerification', e.target.value)} />
                  </Field>
                  <Field label="Bing Site Verification" hint="Kode meta dari Bing Webmaster">
                    <input className="as-input" value={seo.bingVerification || ''} onChange={e => setSeo('bingVerification', e.target.value)} />
                  </Field>
                </div>

                <div className="as-section-title" style={{ marginTop: 32 }}>Analytics & Lanjutan</div>
                <div className="as-fields">
                  <Field label="Google Analytics ID (GA4)" hint="Contoh: G-XXXXXXXXXX">
                    <input className="as-input" value={seo.gaMeasurementId || ''} onChange={e => setSeo('gaMeasurementId', e.target.value)} placeholder="G-XXXXXXXXXX" />
                  </Field>
                  <Field label="Theme Color" hint="Warna bar browser mobile">
                    <input className="as-input" value={seo.themeColor || ''} onChange={e => setSeo('themeColor', e.target.value)} placeholder="#001540" />
                  </Field>
                  <Field label="Custom Head Tags (lanjutan)" hint="HTML tag tambahan untuk <head>. Hati-hati — hanya untuk admin teknis.">
                    <textarea className="as-textarea" rows={4} value={seo.customHeadTags || ''} onChange={e => setSeo('customHeadTags', e.target.value)} placeholder="<meta ... />" style={{ fontFamily: 'monospace', fontSize: 12 }} />
                  </Field>
                </div>
              </div>
              );
            })()}

            {activeTab === 'navigasi' && (
              <div className="as-section">
                <div className="as-section-title">Link Eksternal</div>
                <p className="as-section-desc">Link ke situs terkait — tampil di footer dan sidebar. Contoh: inspiratekno.com, InspiraLearn.</p>
                <LinkListEditor
                  value={form.externalLinks || []}
                  onChange={v => set('externalLinks', v)}
                  addLabel="Tambah Link Eksternal"
                />

                <div className="as-section-title" style={{ marginTop: 32 }}>Media Sosial</div>
                <p className="as-section-desc">Link media sosial yang tampil di footer.</p>
                <LinkListEditor
                  value={form.socialLinks || []}
                  onChange={v => set('socialLinks', v)}
                  addLabel="Tambah Media Sosial"
                />

                <div className="as-section-title" style={{ marginTop: 32 }}>Link Tambahan Footer</div>
                <p className="as-section-desc">Link custom yang tampil di kolom Platform footer.</p>
                <LinkListEditor
                  value={form.footerLinks || []}
                  onChange={v => set('footerLinks', v)}
                  addLabel="Tambah Link Footer"
                />
              </div>
            )}

            {activeTab === 'fitur' && (
              <div className="as-section">
                <div className="as-section-title">Fitur Platform</div>
                <p className="as-section-desc">Aktifkan atau nonaktifkan fitur platform. Berlaku segera setelah disimpan.</p>
                <div className="as-feature-list">
                  {FEATURE_FIELDS.map(f => (
                    <div key={f.key} className="as-feature-item">
                      <div className="as-feature-text">
                        <div className="as-feature-label">{f.label}</div>
                        <div className="as-feature-desc">{f.desc}</div>
                      </div>
                      <button
                        type="button"
                        className={`as-toggle${form[f.key] ? ' on' : ''}`}
                        onClick={() => set(f.key, !form[f.key])}
                      >
                        {form[f.key]
                          ? <><ToggleRight size={28} /><span>Aktif</span></>
                          : <><ToggleLeft size={28} /><span>Nonaktif</span></>
                        }
                      </button>
                    </div>
                  ))}
                </div>

                <div className="as-section-title" style={{ marginTop: 32 }}>Metode Pembayaran</div>
                <p className="as-section-desc">Pilih cara pembayaran pengajuan artikel oleh reader.</p>
                <div className="as-fields">
                  <Field label="Mode Pembayaran Aktif" hint="Berlaku untuk pengajuan artikel baru">
                    <select className="as-input" value={form.paymentMode === 'free' ? 'midtrans' : (form.paymentMode || 'midtrans')} onChange={e => set('paymentMode', e.target.value)}>
                      <option value="midtrans">Midtrans (otomatis)</option>
                      <option value="manual">Transfer Manual (verifikasi admin)</option>
                    </select>
                  </Field>
                  <Field label="Nominal Pembayaran (Rp)" hint="Biaya pengajuan artikel">
                    <input className="as-input" type="number" min={0} value={form.paymentAmount ?? 150000} onChange={e => set('paymentAmount', parseInt(e.target.value) || 0)} />
                  </Field>
                  <Field label="Harga Coret" hint="Tampilkan harga lama yang dicoret di samping nominal pembayaran. Nominal yang ditagih tetap Nominal Pembayaran di atas.">
                    <button
                      type="button"
                      className={`as-toggle${form.priceStrikeEnabled ? ' on' : ''}`}
                      onClick={() => set('priceStrikeEnabled', !form.priceStrikeEnabled)}
                    >
                      {form.priceStrikeEnabled
                        ? <><ToggleRight size={28} /><span>Aktif</span></>
                        : <><ToggleLeft size={28} /><span>Nonaktif</span></>
                      }
                    </button>
                  </Field>
                  {form.priceStrikeEnabled && (
                    <Field label="Nominal Harga Coret (Rp)" hint="Harga sebelum potongan, misalnya 350000">
                      <input className="as-input" type="number" min={0} value={form.priceStrikeAmount ?? 0} onChange={e => set('priceStrikeAmount', parseInt(e.target.value) || 0)} />
                      {(form.priceStrikeAmount || 0) > (form.paymentAmount ?? 150000) ? (
                        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--in-text-muted)' }}>
                          Pratinjau:{' '}
                          <s style={{ color: 'var(--in-text-faint)' }}>Rp {Number(form.priceStrikeAmount).toLocaleString('id-ID')}</s>{' '}
                          <strong style={{ color: 'var(--in-text)' }}>Rp {Number(form.paymentAmount ?? 150000).toLocaleString('id-ID')}</strong>
                        </div>
                      ) : (
                        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--in-danger, #DC2626)' }}>
                          Harga coret harus lebih besar dari Nominal Pembayaran agar ditampilkan.
                        </div>
                      )}
                    </Field>
                  )}
                  {form.paymentMode === 'manual' && (
                    <>
                      <Field label="Nama Bank">
                        <input className="as-input" value={form.bankName || ''} onChange={e => set('bankName', e.target.value)} placeholder="Bank Mandiri" />
                      </Field>
                      <Field label="Nomor Rekening">
                        <input className="as-input" value={form.bankAccountNo || ''} onChange={e => set('bankAccountNo', e.target.value)} placeholder="1300 5 2112 2112" />
                      </Field>
                      <Field label="Atas Nama">
                        <input className="as-input" value={form.bankAccountName || ''} onChange={e => set('bankAccountName', e.target.value)} placeholder="PT. Nusa Inspira Teknologi" />
                      </Field>
                      <Field label="Instruksi Pembayaran" hint="Petunjuk untuk reader">
                        <textarea className="as-textarea" rows={3} value={form.paymentInstruction || ''} onChange={e => set('paymentInstruction', e.target.value)} />
                      </Field>
                    </>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
